/**
 * Compass — Strategy-to-Priority Alignment analyzer.
 *
 * Compass holds the priority list up against what the organisation actually said
 * its strategy is. It reads strategic_objectives and the objective↔priority
 * mappings and surfaces — deterministically — where stated strategy and real
 * priorities have quietly diverged:
 *
 *   orphaned_objective   A critical/high objective with NOTHING mapped to it.
 *                        You said it matters; the backlog disagrees.
 *   under_owned          A critical/high objective that has only weak (indirect)
 *                        links — nominally covered, concretely owned by no one.
 *   unbacked_priorities  Active items serving no objective at all — effort with no
 *                        strategic rationale.
 *   portfolio_imbalance  A low-priority objective hoarding primary effort while a
 *                        critical one starves.
 *
 * The mappings (alignment_strength ∈ primary | secondary | indirect) are exact;
 * the LLM only narrates.
 *
 * The pure detection core lives in supabase/functions/_shared/intelligenceCores.ts
 * (shared with the ambient edge function); this file keeps the client-side data
 * loading and batch orchestration.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { createFinding, clearActiveFindings, type NewFinding } from '../agentFindingsService';
import { narrateFinding } from './llmIntelligenceService';
import {
  detectCompassFindings,
  COMPASS_PERSONA,
  MAX_FINDINGS,
  type ObjPriority,
  type Strength,
  type Objective,
  type Mapping,
} from '../../../supabase/functions/_shared/intelligenceCores';

export const COMPASS_AGENT_TYPE = 'compass';

// Re-export so existing importers of this module keep working unchanged.
export { detectCompassFindings };

// ── Data loading ─────────────────────────────────────────────────────────────────

async function load(workspaceId: string, scope: RunScope): Promise<{
  objectives: Objective[];
  mappings: Mapping[];
  itemTitles: Map<string, string>;
  activeItemIds: string[];
}> {
  const supabase = getSupabaseClient();

  const { data: objRows } = await supabase
    .from('strategic_objectives')
    .select('id, name, priority, time_horizon, status')
    .eq('workspace_id', workspaceId);

  const objectives: Objective[] = ((objRows ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as string,
    name: (r.name as string) ?? 'Objective',
    priority: ((r.priority as string) ?? 'medium') as ObjPriority,
    timeHorizon: (r.time_horizon as string) ?? '',
    status: (r.status as string) ?? 'active',
  }));

  const objIds = objectives.map((o) => o.id);
  let mappings: Mapping[] = [];
  if (objIds.length) {
    const { data: mapRows } = await supabase
      .from('objective_priority_mappings')
      .select('objective_id, priority_id, alignment_strength')
      .in('objective_id', objIds);
    mappings = ((mapRows ?? []) as Record<string, unknown>[]).map((r) => ({
      objectiveId: r.objective_id as string,
      priorityId: r.priority_id as string,
      strength: ((r.alignment_strength as string) ?? 'indirect') as Strength,
    }));
  }

  let itemQuery = supabase
    .from('items')
    .select('id, title')
    .eq('workspace_id', workspaceId)
    .in('exit_status', ['active', 'in_review', 'blocked'])
    .not('lifecycle_state', 'eq', 'deleted');
  if (scope.type === 'date_range') {
    if (scope.from) itemQuery = itemQuery.gte('created_at', scope.from);
    if (scope.to)   itemQuery = itemQuery.lte('created_at', scope.to);
  }
  const { data: itemRows } = await itemQuery.limit(5000);

  const itemTitles = new Map<string, string>();
  const activeItemIds: string[] = [];
  for (const r of (itemRows ?? []) as Record<string, unknown>[]) {
    const id = r.id as string;
    itemTitles.set(id, (r.title as string) ?? '');
    activeItemIds.push(id);
  }
  // Ensure mapped items have titles too (they may be outside the active scope).
  const missing = mappings.map((m) => m.priorityId).filter((id) => !itemTitles.has(id));
  if (missing.length) {
    const { data: extraRows } = await supabase.from('items').select('id, title').in('id', [...new Set(missing)]);
    for (const r of (extraRows ?? []) as Record<string, unknown>[]) {
      itemTitles.set(r.id as string, (r.title as string) ?? '');
    }
  }

  return { objectives, mappings, itemTitles, activeItemIds };
}

// ── Batch runner ─────────────────────────────────────────────────────────────────

export async function runCompassBatch(
  workspaceId: string,
  scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };

  const { objectives, mappings, itemTitles, activeItemIds } = await load(workspaceId, scope);
  stats.processed = objectives.length + activeItemIds.length;

  let drafts = detectCompassFindings(objectives, mappings, itemTitles, activeItemIds);
  drafts = drafts.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);

  if (drafts.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  await clearActiveFindings(workspaceId, COMPASS_AGENT_TYPE);

  const total = drafts.length;
  let done = 0;
  for (const draft of drafts) {
    if (signal.aborted) break;

    const narration = await narrateFinding({
      persona: COMPASS_PERSONA,
      findingKind: draft.findingKind,
      evidence: draft.base.data ?? {},
      systemPrompt: config.systemPrompt,
      model: config.model,
    });

    const finding: NewFinding = {
      ...draft.base,
      summary: narration?.summary || draft.fallbackSummary,
      detail: narration?.detail || draft.fallbackDetail,
    };

    const { error } = await createFinding(workspaceId, COMPASS_AGENT_TYPE, finding);
    if (error) stats.errors++; else stats.proposed++;

    done++;
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return stats;
}
